import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, Send, ScrollText, ExternalLink } from "lucide-react";
import { DataStatusChip } from "@/components/common/DataStatusChip";

/**
 * AIAgentPanel — reusable "Ask the {module} Agent" slide-over.
 *
 * Wraps `/api/ai-agents/{agentId}/analyse`.
 *
 * <AIAgentPanel agentId="crm" agentName="CRM Agent" context={{client_id: '...'}} />
 */
// Per-agent suggested questions so each agent panel prompts the exec
// with questions that actually make sense for the module they're on.
// (Bug: Feb 2026 — every panel was showing CRM prompts regardless of
// which agent it wrapped.)
const SUGGESTED_BY_AGENT = {
  crm: [
    "Which clients need attention?",
    "Any cross-sell opportunities?",
    "Explain the health score for the currently selected client.",
    "Which clients look like they are declining?",
  ],
  financial: [
    "Summarise this month's P&L vs budget.",
    "Who are our top 5 debtors over 90 days?",
    "Which BUs are dragging group GP% down?",
    "Where is cash trending vs the last 12 months?",
  ],
  executive: [
    "Give me a 4-bullet board update on this week.",
    "What are the biggest risks across the group right now?",
    "Which BUs are ahead of budget and which are behind?",
    "Any client concentration risks I should be aware of?",
  ],
  operations: [
    "What jobs are overdue and by how many days?",
    "Which BU has the biggest overdue-jobs backlog?",
    "Where is our WIP trending vs last month?",
    "Any reactive work that looks like it should be planned?",
  ],
  hseq: [
    "Any HSEQ incidents in the last 7 days?",
    "Which corrective actions are overdue?",
    "Are we tracking to zero LTI this FY?",
    "Which sites have the most open HSEQ actions?",
  ],
  sustainability: [
    "How is our fuel consumption trending FYTD?",
    "Who are the top / bottom drivers on efficiency?",
    "What's our CO2e vs the same period last year?",
    "Which vehicles are consuming disproportionately?",
  ],
  assets: [
    "Which assets are flagged high-risk?",
    "What's expiring in the next 90 days?",
    "Any assets with rising maintenance costs?",
  ],
  hr: [
    "Whose licences are expiring in the next 30 days?",
    "What's our workforce compliance %?",
    "Any employees overdue for induction refresh?",
  ],
};

const DEFAULT_SUGGESTED = [
  "Give me a short summary of this module right now.",
  "What are the biggest risks or issues in this data?",
  "Which items need attention this week?",
];

export function AIAgentPanel({ agentId, agentName, context = {}, defaultQuestion, buttonLabel = "Ask AI", buttonVariant = "outline", buttonSize = "sm", testid }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState(defaultQuestion || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const suggested = SUGGESTED_BY_AGENT[agentId] || DEFAULT_SUGGESTED;

  const ask = async (q) => {
    setLoading(true); setError(null); setResult(null);
    try {
      // LLM calls (Claude Sonnet) can legitimately take 30-90 seconds
      // especially when the context builder scans myob_invoices. Override
      // the global 20 s axios timeout so the agent panel actually waits.
      const { data } = await api.post(
        `/ai-agents/${agentId}/analyse`,
        { question: q, context },
        { timeout: 120000 },
      );
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button
        data-testid={testid || `ai-agent-btn-${agentId}`}
        onClick={() => setOpen(true)}
        variant={buttonVariant}
        size={buttonSize}
        className={buttonVariant === "outline"
          ? "border-violet-300 text-violet-800 hover:bg-violet-50 hover:text-violet-900"
          : "bg-gradient-to-br from-violet-600 to-violet-800 text-white hover:from-violet-700 hover:to-violet-900"}
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
        {buttonLabel}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <span className="text-[10px] uppercase tracking-widest text-violet-700 font-medium">AI Agent</span>
              <DataStatusChip status="ai_generated" size="xs" className="ml-auto" />
            </div>
            <SheetTitle className="font-display text-lg">{agentName}</SheetTitle>
            <SheetDescription className="text-xs text-slate-500">
              Powered by Claude Sonnet · Cites Enterprise IDs · Every run is logged for audit
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 py-5 space-y-4">
            {!result && !loading && (
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Suggested questions</label>
                <div className="grid gap-1.5">
                  {suggested.map((s) => (
                    <button key={s}
                      data-testid={`ai-agent-suggested-${agentId}`}
                      onClick={() => { setQuestion(s); ask(s); }}
                      className="text-left text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md px-3 py-2 text-slate-700">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Ask a question</label>
              <Textarea
                data-testid={`ai-agent-input-${agentId}`}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={`Ask ${agentName} anything about this module…`}
                className="min-h-20 mt-1 text-sm"
                disabled={loading}
              />
              <div className="mt-2 flex gap-2">
                <Button
                  data-testid={`ai-agent-send-${agentId}`}
                  onClick={() => ask(question)}
                  disabled={!question.trim() || loading}
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  {loading ? "Thinking…" : "Ask"}
                </Button>
                {result && (
                  <Button variant="outline" size="sm" onClick={() => { setResult(null); setQuestion(""); }}>
                    New question
                  </Button>
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                {error}
              </div>
            )}

            {result && !loading && (
              <div data-testid={`ai-agent-result-${agentId}`} className="rounded-lg border border-violet-200 bg-white overflow-hidden">
                <div className="px-4 py-2 bg-violet-50/70 border-b border-violet-100 flex items-center gap-2 text-[10px] uppercase tracking-wider">
                  <span className="text-violet-700 font-medium">Answer</span>
                  <span className="text-slate-500">·</span>
                  <span className={`font-medium ${result.confidence_label === "HIGH" ? "text-emerald-700" : result.confidence_label === "MEDIUM" ? "text-amber-700" : "text-rose-700"}`}>
                    {result.confidence_label} confidence · {result.confidence}%
                  </span>
                  <span className="ml-auto text-slate-500">{result.model}</span>
                </div>
                <div className="px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {result.answer}
                </div>
                {result.context_refs?.length > 0 && (
                  <div className="px-4 py-2 border-t border-slate-100 flex flex-wrap gap-1.5 text-[10px]">
                    <span className="text-slate-500 uppercase tracking-wider mr-1">Refs:</span>
                    {result.context_refs.map((r) => (
                      <span key={r} className="font-mono bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{r}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="pt-3 border-t border-slate-100 text-[10px] text-slate-400 flex items-center gap-1">
              <ScrollText className="w-3 h-3" />
              All AI runs are logged in
              <a href="/admin/data-governance" className="underline text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                Governance <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
